/**
 * BankFeedPanel — Enable Banking (PSD2) bank feed status + controls.
 *
 * Multiple connections live side by side (ING business profile covering the
 * corporate accounts + ING personal profile). Each has its own ~180-day
 * consent. Buttons:
 *   • Connect business / personal — starts an ING authorization (SCA tab)
 *   • Sync now — runs the balance/transaction sync across ALL connections
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../../utils/api';

const fmtEur = (v) =>
  v == null ? '—' : `€${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const fmtWhen = (v) => (v ? new Date(v).toLocaleString('en-GB') : 'never');

const BankFeedPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'business' | 'personal' | 'sync'
  const [message, setMessage] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiGet('/api/eb/status');
      setStatus(data);
    } catch (e) {
      setStatus({ success: false, error: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleConnect = async (psuType) => {
    setBusy(psuType);
    setMessage(null);
    try {
      const resp = await apiPost('/api/eb/connect', { psu_type: psuType });
      if (resp.url) {
        window.open(resp.url, '_blank', 'noopener');
        setMessage(`Approve the ${psuType} access in the ING tab that just opened, then come back and hit Refresh.`);
      } else {
        setMessage(`Connect failed: ${resp.message || 'no authorization URL returned'}`);
      }
    } catch (e) {
      setMessage(`Connect failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleSync = async () => {
    setBusy('sync');
    setMessage(null);
    try {
      const resp = await apiPost('/api/eb/sync', {});
      const rep = resp.report || {};
      if (rep.error) {
        setMessage(`Sync: ${rep.error}`);
      } else {
        const parts = (rep.accounts || []).map((a) =>
          a.error ? `${a.label || a.iban}: ERROR ${a.error}`
            : a.skipped ? `${a.iban}: ${a.skipped}`
            : `${a.label || a.iban}: ${fmtEur(a.balance)}`
        );
        const txs = (rep.accounts || []).find((a) => a.transactions);
        setMessage(
          `Sync ${rep.ok ? 'OK' : 'finished with errors'} — ${parts.join(' · ')}` +
          (txs ? ` · GryKriCa tx: +${txs.transactions.inserted} new (from ${txs.transactions.date_from})` : '') +
          ((rep.warnings || []).length ? ` · ⚠ ${rep.warnings.join(' / ')}` : '')
        );
      }
      await refresh();
    } catch (e) {
      setMessage(`Sync failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const connections = status?.connections || [];
  const worstDays = connections.reduce(
    (min, c) => (c.days_left != null && (min == null || c.days_left < min) ? c.days_left : min),
    null
  );
  const badgeClass =
    worstDays == null ? 'ebf-ok' : worstDays <= 0 ? 'ebf-expired' : worstDays <= 14 ? 'ebf-warning' : 'ebf-ok';

  return (
    <div className="admin-section">
      <div className="section-header">
        <h3>Bank Feed — Enable Banking</h3>
        {loading ? null : connections.length ? (
          <span className={`ebf-badge ${badgeClass}`}>
            {connections.length} connection{connections.length > 1 ? 's' : ''}
            {worstDays != null ? ` · ${worstDays}d left` : ''}
          </span>
        ) : (
          <span className="ebf-badge ebf-expired">Not connected</span>
        )}
      </div>

      {loading ? (
        <p>Loading bank feed status…</p>
      ) : (
        <>
          {connections.map((c, i) => (
            <div key={i} className="ebf-accounts">
              <div className="ebf-conn-title">
                ING · {c.psu_type} — valid until {c.valid_until ? new Date(c.valid_until).toLocaleDateString('en-GB') : '—'}
                {c.days_left != null && c.days_left <= 14 && (
                  <span className="ebf-badge ebf-warning" style={{ marginLeft: 8 }}>{c.days_left}d left — renew</span>
                )}
              </div>
              {(c.accounts || []).map((a) => (
                <div key={a.iban || a.name} className="ebf-account-row">
                  <span className="ebf-iban">{a.iban || a.name}</span>
                  <span className="ebf-balance">{fmtEur(a.last_balance_eur)}</span>
                  <span className="ebf-sync">synced {fmtWhen(a.last_balance_sync)}</span>
                </div>
              ))}
            </div>
          ))}

          {!connections.length && (
            <p className="ebf-meta">
              No active bank connection. Use the buttons below — each opens an ING authorization
              (read-only account access, ~180 days). Business covers the corporate accounts;
              personal covers your private account. They stay connected side by side.
            </p>
          )}

          <div className="ebf-actions">
            <button className="approve-button" onClick={() => handleConnect('business')} disabled={busy !== null}>
              {busy === 'business' ? 'Opening ING…' : 'Connect / renew business'}
            </button>
            <button className="approve-button" onClick={() => handleConnect('personal')} disabled={busy !== null}>
              {busy === 'personal' ? 'Opening ING…' : 'Connect / renew personal'}
            </button>
            <button className="retry-button" onClick={handleSync} disabled={busy !== null || !connections.length}>
              {busy === 'sync' ? 'Syncing…' : 'Sync now'}
            </button>
            <button className="retry-button" onClick={() => { setLoading(true); refresh(); }} disabled={busy !== null}>
              Refresh
            </button>
          </div>

          <div className="ebf-meta">Auto-sync every 6h (01:20 / 07:20 / 13:20 / 19:20 UK).</div>

          {message && <div className="ebf-message">{message}</div>}
        </>
      )}
    </div>
  );
};

export default BankFeedPanel;
